<script lang="ts">
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";
  import FeaturedFilter from "@shared/svelte/Components/ProductLines/FeaturedFilter.svelte";
  import Filter from "@shared/svelte/Components/ProductLines/Filter.svelte";
  import FiltersHeader from "@shared/svelte/Components/ProductLines/FiltersHeader.svelte";
  import {
    ProductLineListingStore,
    updateProductLineListingStore,
  } from "@retailer/svelte/ProductLines/ProductLineContainer/stores/ProductLineListingStore";
  import SelectedFiltersChips from "@shared/svelte/Components/ProductLines/SelectedFiltersChips.svelte";
  import * as _ from "lodash";
  import {
    filterFiltersByEnabledRollouts,
    filterFilterTypesFromStateParams,
    filterSelectedFiltersFromStateParams,
    shouldShowFilters,
  } from "@shared/services/filters";
  import { createEventDispatcher, onDestroy } from "svelte";
  import { get as getStoreValue } from "svelte/store";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  import type { FiltersType, FilterTypesType } from "@shared/types/Filters";

  const dispatch = createEventDispatcher();

  export let filters: FiltersType;
  export let stateParams: any = {};
  export let onChange: any;
  export let onSearch: any = null;
  export let onClose: any = null;
  export let onApply: any = null;
  export let showFiltersInMobile: boolean = false;
  export let mobileMode: boolean = false;
  export let autoExpandInDesktop: boolean = false;
  export let disableFiltersDuringProcessing = false;
  export let updateOnLoad: boolean = false;
  export let disableFilters: boolean = false;
  export let disableClearButton: boolean = false;
  export let disableApplyButton: boolean = false;
  // To let components using filters know which filters to ignore.
  export let disabledFilterRolloutIds = [];
  // To support excluding specified filter keys from render
  export let excludedKeys: string[] = [];
  export let showIfNoResults: boolean = true;

  const isDistributorPlp = stateParams.distributors?.length;

  let productsFound = false;
  let displayFilters = true;
  const unsubscribe = ProductLineListingStore.subscribe(n => {
    productsFound = n.productsFound;
    displayFilters = shouldShowFilters(n.selectedFilters, n.productsFound) || showIfNoResults;
  });

  onDestroy(() => {
    unsubscribe();
  });

  let currentUser: any = getStoreValue(CurrentUserStore);

  // When removing the rollout, set enable: true in the filters service file.
  let filterRollouts = [
    { id: "future_pricing", enable: stagedRolloutCheck("search__future_pricing_filter") },
  ];
  disabledFilterRolloutIds = filterRollouts.filter(r => !r.enable).map(r => r.id);

  let filterTypes: FilterTypesType[] = filterFiltersByEnabledRollouts(filterRollouts);
  filterTypes = filterFilterTypesFromStateParams(stateParams, filterTypes, filters, excludedKeys);

  filters.selected_filters = filterSelectedFiltersFromStateParams(stateParams, filters);
  updateProductLineListingStore(n => {
    n.selectedFilters = filters.selected_filters;
    n.onClearFilter = clearFilter;
    n.onClearAllFilters = clearFilters;
    n.onApplyFilters = onApply;
    return n;
  });

  const filtersCount: number = filters.selected_filters.length;

  function setupFilter(id, filter, newStateParams) {
    let param = filter.param;
    if (!filter.single) {
      if (newStateParams[param] === undefined || newStateParams[param] === null) {
        newStateParams[param] = [];
      } else {
        if (!_.isArray(newStateParams[param])) {
          newStateParams[param] = [newStateParams[param]];
        }
      }
    }
    if (filter.featured) {
      if (newStateParams[param].length === 0) {
        newStateParams[param] = "true";
      } else {
        newStateParams[param] = undefined;
      }
    } else {
      if (filter.single) {
        id = String(id);
        if (newStateParams[param] === id) {
          newStateParams[param] = undefined;
        } else {
          newStateParams[param] = id;
        }
      } else {
        if (newStateParams[param].indexOf(String(id)) == -1) {
          newStateParams[param].push(String(id));
        } else {
          let idx = newStateParams[param].indexOf(String(id));
          newStateParams[param].splice(idx, 1);
          if (newStateParams[param].length === 0) {
            newStateParams[param] = undefined;
          }
        }
      }
    }
  }

  function clearFilters() {
    let newStateParams = _.cloneDeep(stateParams);
    filters.selected_filters.forEach(item => setupFilter(item.id, item.filter, newStateParams));
    changeFilters(newStateParams);
    Object.assign(stateParams, newStateParams);
  }

  function clearFilter(item: any) {
    updateFilter(item.filter, item);
  }

  function findSelected(filters, filter) {
    let stateParamValues: any;
    if (
      stateParams[filter.param] === undefined ||
      stateParams[filter.param] === null ||
      filters === undefined
    ) {
      return [];
    } else {
      stateParamValues = _.isArray(stateParams[filter.param])
        ? stateParams[filter.param]
        : [stateParams[filter.param]];
    }
    if (filter.featured) {
      return [filter];
    }
    if (filter.id === "vintage_year") {
      return filters;
    }
    if (filter.id === "abv") {
      return filters;
    }

    return stateParamValues
      .map((v: any) =>
        _.cloneDeep(filters.find((s: any) => s.id == (v.match(/^\d+$/) ? Number(v) : v))),
      )
      .filter(f => f !== undefined);
  }

  function updateSelected(filters, filter) {
    let selectedItems = findSelected(filters, filter);
    for (let item of selectedItems) {
      if (!item) {
        continue;
      }
      let obj = filters.find((p: any) => p.id == item.id);
      if (obj) {
        obj.checked = true;
      }
    }
  }

  function changeFilters(newStateParams) {
    disableFilters = disableFiltersDuringProcessing;
    if (onChange) {
      onChange(newStateParams);
    }
  }

  function updateFilter(filter, item: any) {
    let newStateParams = _.cloneDeep(stateParams);
    setupFilter(item.id, filter, newStateParams);
    delete newStateParams["page"];
    if (filter?.key != null) {
      newStateParams.fired_filter = filter.key;
    } else {
      delete newStateParams["fired_filter"];
    }

    changeFilters(newStateParams);
    Object.assign(stateParams, newStateParams);
  }

  function updateSearch(filter, searchText: any) {
    let newStateParams = _.cloneDeep(stateParams);
    newStateParams[`${filter.id}Search`] = searchText;

    if (onSearch === null) {
      changeFilters(newStateParams);
    } else {
      disableFilters = true;
      onSearch(newStateParams);
    }

    Object.assign(stateParams, newStateParams);
  }

  function close() {
    showFiltersInMobile = false;

    if (onClose) {
      onClose();
    }
  }

  function apply() {
    if (onApply) {
      onApply();
    }

    close();
  }

  dispatch("filterCountChange", {
    filterCount: filters.selected_filters.length,
  });

  function setFilterExpanded(filterIndex, filter): boolean {
    if (filters.selected_filters.find(f => f.filter.param === filter.param)) {
      return true;
    }
    if (filter.id === "sale_division") {
      return isDistributorPlp;
    }
    return filterIndex < 4 && !mobileMode && autoExpandInDesktop;
  }

  $: showDealsFilter =
    (filters.has_discounts || stateParams.dl) &&
    !(currentUser.market?.state == "CT") &&
    !excludedKeys.includes("deals");

  $: showPreviouslyOrdered =
    ((filters["previously_ordered"] && productsFound) || stateParams.po) &&
    !excludedKeys.includes("previously_ordered");

  $: showMyDistributors =
    ((filters["my_distributors"] && productsFound && !isDistributorPlp) || stateParams.md) &&
    !excludedKeys.includes("my_distributors");

  function handleDealToggle() {
    updateDealsFilter();
  }

  const updateDealsFilter = updateFeaturedFilter({ featured: true, param: "dl" });
  const updatePreviouslyOrderedFilter = updateFeaturedFilter({ featured: true, param: "po" });
  const updateMyDistributorsFilter = updateFeaturedFilter({ featured: true, param: "md" });

  function updateFeaturedFilter(filter) {
    return () => updateFilter(filter, {});
  }

  function availableFilterTypes() {
    return filterTypes.filter(
      f => filters[`${f.id}_count`] > 0 || (stateParams[`${f.id}Search`] || "").length > 0,
    );
  }

  if (updateOnLoad) {
    changeFilters(stateParams);
  }
</script>

<style>
  .product-lines-filters-section-mobile {
    height: 70vh;
    overflow: scroll;
  }

  fieldset {
    display: grid;
    grid-template-columns: var(--sidebar-max-width);
  }
</style>

<fieldset data-testid="product-lines-filters" disabled={disableFilters}>
  {#if displayFilters}
    <div class="fizz-inset-horizontal-16">
      <FiltersHeader
        onClear={clearFilters}
        onClose={close}
        selectedFiltersLength={filters.selected_filters.length}
      />
      {#if !mobileMode}
        <SelectedFiltersChips bind:selectedFilters={filters.selected_filters} />
      {/if}
    </div>

    <div class:product-lines-filters-section-mobile={mobileMode}>
      {#if showPreviouslyOrdered || showDealsFilter || showMyDistributors}
        <div class="fizz-card side-filter fizz-inset-vertical-8" id="featured-filter">
          <details class="fizz-accordion" open={true}>
            <summary class="side-filter-header">
              <span class="fizz-heading-6">Featured</span>
            </summary>
            <div class="fizz-inset-horizontal-16 fizz-flow-8">
              <dl class="fizz-overflow-y side-filter__list">
                <FeaturedFilter
                  show={showPreviouslyOrdered}
                  htmlId="buy-it-again-filter"
                  text="Previously ordered"
                  name="previously_ordered"
                  checked={stateParams.po}
                  onChange={updatePreviouslyOrderedFilter}
                  count={filters.previously_ordered_total}
                />
                <FeaturedFilter
                  show={showDealsFilter}
                  htmlId="deals-filter"
                  text="Deals"
                  name="deals-filter"
                  checked={stateParams.dl}
                  onChange={handleDealToggle}
                  count={filters.has_discounts_total}
                />
                <FeaturedFilter
                  show={showMyDistributors}
                  htmlId="distributors-filter"
                  text="My Distributors"
                  name="distributors_filter"
                  checked={stateParams.md}
                  onChange={updateMyDistributorsFilter}
                  count={filters.my_distributors_total}
                />
              </dl>
            </div>
          </details>
        </div>
      {/if}

      {#each availableFilterTypes() as filter, i}
        {#if filter.component}
          <svelte:component
            this={filter.component}
            {filter}
            {updateFilter}
            filters={filters[filter.id]}
            checked={stateParams[filter.param]}
            filterExpanded={setFilterExpanded(i, filter)}
          />
        {:else}
          <Filter
            count={filters[`${filter.id}_count`]}
            filters={filters[filter.id]}
            {filter}
            filterExpanded={setFilterExpanded(i, filter)}
            {updateFilter}
            {updateSearch}
            {updateSelected}
            searchText={stateParams[`${filter.id}Search`] || ""}
            filterIndex={i}
          />
        {/if}
      {/each}
    </div>

    {#if mobileMode}
      <div class="fizz-border-top fizz-flex-row fizz-space-between fizz-inset-square-16">
        <button
          data-testid="clear-all-filters"
          type="button"
          class="fizz-button fizz-full-width"
          disabled={disableClearButton}
          on:click={clearFilters}
        >
          Clear all
        </button>
        <button
          data-testid="apply-filters"
          type="button"
          class="fizz-button-secondary fizz-full-width"
          disabled={disableApplyButton}
          on:click={apply}
        >
          Apply{filtersCount > 0 ? ` (${filtersCount})` : ""}
        </button>
      </div>
    {/if}
  {/if}
</fieldset>
